import type { ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import { PanelHeader, PanelRow } from '@/components/settings/panel-rows'
import {
  IconCircle,
  IconExternalLink,
  IconFacebook,
  IconInstagram,
  IconLinkedin,
  IconTiktok,
  IconX,
  IconYoutube,
} from '@/components/ui/icons'
import { cn } from '@/lib/utils'

interface AboutPageProps {
  onBack: () => void
}

/** Row with a round leading badge, a label and an external-link button. */
function LinkRow({
  label,
  href,
  icon: Icon = IconCircle,
  withBadge = false,
  last = false,
}: {
  label: string
  href: string
  icon?: React.ElementType
  withBadge?: boolean
  last?: boolean
}): ReactElement {
  return (
    <PanelRow last={last} interactive about>
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className={cn('flex w-full items-center', withBadge ? 'gap-3' : 'gap-4')}
      >
        {withBadge && (
          <span className="bg-btn-secondary-raised hub-stroke text-icon flex shrink-0 items-center justify-center rounded-full p-2">
            <Icon className="size-5" aria-hidden="true" />
          </span>
        )}
        <span className="hub-title-lg text-list-text min-w-px flex-1">{label}</span>
        <span className="text-btn-icon flex shrink-0 items-center justify-center p-1.5">
          <IconExternalLink className="size-4" aria-hidden="true" />
        </span>
      </a>
    </PanelRow>
  )
}

const SOCIAL_CONFIG = {
  facebook: {
    icon: IconFacebook,
    url: import.meta.env.VITE_FACEBOOK_URL as string,
  },
  linkedin: {
    icon: IconLinkedin,
    url: import.meta.env.VITE_LINKEDIN_URL as string,
  },
  youtube: {
    icon: IconYoutube,
    url: import.meta.env.VITE_YOUTUBE_URL as string,
  },
  instagram: {
    icon: IconInstagram,
    url: import.meta.env.VITE_INSTAGRAM_URL as string,
  },
  tiktok: {
    icon: IconTiktok,
    url: import.meta.env.VITE_TIKTOK_URL as string,
  },
  x: {
    icon: IconX,
    url: import.meta.env.VITE_X_URL as string,
  },
} as const

type SocialKey = keyof typeof SOCIAL_CONFIG

export function AboutPage({ onBack }: AboutPageProps): ReactElement {
  const { t } = useTranslation()

  const socials = Object.keys(SOCIAL_CONFIG) as SocialKey[]

  return (
    <>
      <PanelHeader compact title={t('help.about')} onBack={onBack} />

      {socials.map((key) => {
        const { icon, url } = SOCIAL_CONFIG[key]
        return (
          <LinkRow withBadge key={key} icon={icon} label={t(`help.socials.${key}`)} href={url} />
        )
      })}

      <LinkRow label={t('help.termsOfUse')} href={'https://uni-corvinus.hu/hub/'} />
      <LinkRow last label={t('help.privacyPolicy')} href={'https://uni-corvinus.hu/hub/'} />
    </>
  )
}
